import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Chip, Grid,
  CircularProgress, IconButton, Tooltip, Divider,
} from '@mui/material';
import {
  ArrowBack, Login as LoginIcon, Logout as LogoutIcon,
  CheckCircle, Cancel, AccessTime, BeachAccess, EventBusy,
  Timer, TrendingDown, TrendingUp, Warning,
} from '@mui/icons-material';
import { InvokeApi, apiList } from '../../services/ApiService';
import { Config } from '../../config/Config';
import dayjs from 'dayjs';

const MONTHS = ['January','February','March','April','May','June',
  'July','August','September','October','November','December'];

const statusColor = (s) => {
  const l = (s || '').toLowerCase();
  if (l === 'present')  return '#2e7d32';
  if (l === 'absent')   return '#c62828';
  if (l === 'fine')     return '#6a1b9a';
  if (l === 'half day')     return '#4f2f1e';
  if (l === 'leave')    return '#e65100';
  if (l === 'week off') return '#00695c';
  return '#9e9e9e';
};
const statusBg = (s) => {
  const l = (s || '').toLowerCase();
  if (l === 'present')  return '#e8f5e9';
  if (l === 'absent')   return '#ffebee';
  if (l === 'fine')     return '#f3e5f5';
  if (l === 'half day')     return '#f5f5f5';
  if (l === 'leave')    return '#fff3e0';
  if (l === 'week off') return '#e0f2f1';
  return '#f5f5f5';
};

export default function AttendanceMonthDetail() {
  const { id, month, year } = useParams();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [records, setRecords] = useState([]);
  const [empName, setEmpName] = useState('');
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(parseInt(month));
  const [currentYear, setCurrentYear] = useState(parseInt(year));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [empData, detail] = await Promise.all([
        InvokeApi(Config.main_url + apiList.getEmployee, { id }),
        InvokeApi(Config.main_url + apiList.getMonthlyAttendanceDetail, {
          studentId: id, month: currentMonth, year: currentYear,
        }),
      ]);
      setEmpName(empData?.employee?.[0]?.name || '');
      setSummary(detail?.summary || null);
      setRecords(detail?.records || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [id, currentMonth, currentYear]);

  useEffect(() => { load(); }, [load]);

  const monthName = MONTHS[currentMonth - 1];

  const prevMonth = () => {
    const d = dayjs(`${currentYear}-${currentMonth}-01`).subtract(1, 'month');
    setCurrentMonth(d.month() + 1); setCurrentYear(d.year());
  };
  const nextMonth = () => {
    const d = dayjs(`${currentYear}-${currentMonth}-01`).add(1, 'month');
    setCurrentMonth(d.month() + 1); setCurrentYear(d.year());
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Tooltip title="Back">
          <IconButton onClick={() => navigate(`/employees/${id}`)}
            sx={{ border: '1px solid #e8eaf6', borderRadius: 2 }}>
            <ArrowBack sx={{ color: '#1a237e' }} />
          </IconButton>
        </Tooltip>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight={800} color="#1a237e">{monthName} {currentYear}</Typography>
          <Typography variant="body2" color="text.secondary">{empName} · Emp ID #{id}</Typography>
        </Box>
        {/* Month nav */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <IconButton size="small" onClick={prevMonth} sx={{ border: '1px solid #e8eaf6', borderRadius: 2 }}>
            <ArrowBack fontSize="small" sx={{ color: '#1a237e' }} />
          </IconButton>
          <Typography fontSize={13} fontWeight={600} color="#1a237e" sx={{ minWidth: 110, textAlign: 'center' }}>
            {monthName} {currentYear}
          </Typography>
          <IconButton size="small" onClick={nextMonth} sx={{ border: '1px solid #e8eaf6', borderRadius: 2, transform: 'rotate(180deg)' }}>
            <ArrowBack fontSize="small" sx={{ color: '#1a237e' }} />
          </IconButton>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
          <CircularProgress sx={{ color: '#1a237e' }} />
        </Box>
      ) : (
        <>
          <Grid container spacing={1.5} mb={3}>
            {[
              { label: 'Present',  value: summary?.present,  color: '#2e7d32', bg: '#e8f5e9', icon: <CheckCircle /> },
              { label: 'Absent',   value: summary?.absent,   color: '#c62828', bg: '#ffebee', icon: <Cancel /> },
              { label: 'Fine',     value: summary?.late,     color: '#6a1b9a', bg: '#f3e5f5', icon: <AccessTime /> },
              { label: 'Half Day', value: summary?.half_day, color: '#4f2f1e', bg: '#f5f5f5', icon: <AccessTime /> },
              { label: 'Leave',    value: summary?.leave,    color: '#e65100', bg: '#fff3e0', icon: <BeachAccess /> },
              { label: 'Week Off', value: summary?.week_off || 0, color: '#00695c', bg: '#e0f2f1', icon: <EventBusy /> },
              { label: 'Fines',    value: summary?.total_fines > 0 ? `₹${parseFloat(summary.total_fines).toLocaleString()}` : '₹0', color: '#b71c1c', bg: '#ffebee', icon: <Warning /> },
            ].map(s => (
              <Grid item xs={6} sm={4} md={2} key={s.label}>
                <Card sx={{ borderRadius: 3, boxShadow: '0 4px 16px rgba(0,0,0,0.07)', borderTop: `3px solid ${s.color}` }}>
                  <CardContent sx={{ p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography fontWeight={900} fontSize={s.label === 'Fines' ? 16 : 26} color={s.color} lineHeight={1}>
                        {s.value ?? 0}
                      </Typography>
                      <Typography fontSize={11} fontWeight={700} color={s.color}>{s.label}</Typography>
                    </Box>
                    <Box sx={{ p: 1, borderRadius: 2, background: s.bg }}>
                      {React.cloneElement(s.icon, { sx: { color: s.color, fontSize: 18 } })}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Typography fontWeight={700} color="#1a237e" mb={1.5} fontSize={15}>Day-wise Attendance</Typography>
          {records.length === 0 ? (
            <Typography color="text.secondary" textAlign="center" py={4}>No records for {monthName} {currentYear}</Typography>
          ) : (
            <Grid container spacing={1.5}>
              {records.map((item, i) => {
                const sc = statusColor(item.status);
                const sb = statusBg(item.status);
                const d = item.date ? dayjs(item.date) : null;
                return (
                  <Grid item xs={12} sm={6} md={4} key={i}>
                    <Box sx={{
                      display: 'flex', alignItems: 'center', gap: 1.5,
                      p: 1.5, borderRadius: 2, height: '100%',
                      background: '#fff',
                      borderLeft: `4px solid ${sc}`,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                      border: `1px solid ${sc}20`,
                      borderLeftWidth: 4,
                    }}>
                    {/* Date badge */}
                    <Box sx={{
                      width: 46, height: 46, borderRadius: 2, flexShrink: 0,
                      background: sc,
                      display: 'flex', flexDirection: 'column',
                      alignItems: 'center', justifyContent: 'center',
                    }}>
                      <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: 16, lineHeight: 1 }}>
                        {d ? d.format('DD') : '—'}
                      </Typography>
                      <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: 9, lineHeight: 1.3 }}>
                        {d ? d.format('ddd').toUpperCase() : ''}
                      </Typography>
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 0.5 }}>
                        <Chip label={item.status} size="small"
                          sx={{ background: sb, color: sc, fontWeight: 700, fontSize: 10, height: 20 }} />
                        {item.is_late && (
                          <Chip label={`Late +${item.late_fmt}`} size="small"
                            sx={{ background: '#f3e5f5', color: '#6a1b9a', fontWeight: 700, fontSize: 10, height: 20 }} />
                        )}
                        {item.is_early_logout && (
                          <Chip label={`Early -${item.early_fmt}`} size="small"
                            sx={{ background: '#ffebee', color: '#c62828', fontWeight: 700, fontSize: 10, height: 20 }} />
                        )}
                        {item.has_reduction && (
                          <Chip label={`↑ ${item.reduction_fmt}`} size="small"
                            sx={{ background: '#e8f5e9', color: '#2e7d32', fontWeight: 700, fontSize: 10, height: 20 }} />
                        )}
                        {item.has_deduction && (
                          <Chip label={`↓ ${item.deduction_fmt}`} size="small"
                            sx={{ background: '#fff3e0', color: '#e65100', fontWeight: 700, fontSize: 10, height: 20 }} />
                        )}
                        {item.has_fine && (
                          <Chip
                            icon={<Warning sx={{ fontSize: '11px !important', color: '#b71c1c !important' }} />}
                            label={`Fine ₹${parseFloat(item.day_fines).toLocaleString()} - Added By ${item.fine_added_by}`}
                            size="small"
                            sx={{ background: '#ffebee', color: '#b71c1c', fontWeight: 700, fontSize: 10, height: 20 }}
                          />
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                          <LoginIcon sx={{ fontSize: 13, color: item.check_in ? '#2e7d32' : '#ccc' }} />
                          <Typography fontSize={12} fontWeight={600} color={item.check_in ? '#333' : '#bbb'}>
                            {item.check_in || 'No check-in'}
                          </Typography>
                          {item.check_in && item.expected_in && (
                            <Typography fontSize={10} color="text.secondary">({item.expected_in})</Typography>
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                          <LogoutIcon sx={{ fontSize: 13, color: item.check_out ? '#c62828' : '#ccc' }} />
                          <Typography fontSize={12} fontWeight={600} color={item.check_out ? '#333' : '#bbb'}>
                            {item.check_out || 'No check-out'}
                          </Typography>
                          {item.check_out && item.expected_out && (
                            <Typography fontSize={10} color="text.secondary">({item.expected_out})</Typography>
                          )}
                        </Box>
                      </Box>
                    </Box>

                    {/* Worked hours */}
                    {item.worked_fmt && (
                      <Box sx={{ textAlign: 'center', flexShrink: 0 }}>
                        <Typography fontSize={10} color="text.secondary">Worked</Typography>
                        <Typography fontSize={14} fontWeight={800} color="#1a237e">{item.worked_fmt}</Typography>
                      </Box>
                    )}
                  </Box>
                  {/* Fine reason row */}
                  {/* {item.has_fine && item.fine_reasons && (
                    <Box sx={{ px: 1.5, pb: 1, mt: -0.5 }}>
                      <Typography fontSize={10} color="#b71c1c" fontStyle="italic">
                        📌 {item.fine_reasons}
                      </Typography>
                    </Box>
                  )} */}
                  </Grid>
                );
              })}
            </Grid>
          )}

          {summary && (
            <Card sx={{ borderRadius: 3, boxShadow: '0 4px 16px rgba(0,0,0,0.08)', mt: 3 }}>
              <CardContent sx={{ p: 0 }}>
                <Box sx={{ px: 3, py: 2, borderBottom: '1px solid #f0f0f0' }}>
                  <Typography fontWeight={700} color="#1a237e" fontSize={15}>Time Summary & Deductions</Typography>
                </Box>
                {[
                  { label: 'Total Worked',       value: summary.total_worked_fmt,    color: '#2e7d32', icon: <Timer /> },
                  { label: 'Total Late Login',   value: summary.total_late_fmt,      color: '#6a1b9a', icon: <TrendingDown /> },
                  { label: 'Total Early Logout', value: summary.total_early_fmt,     color: '#c62828', icon: <TrendingDown /> },
                  { label: 'Total Reduced',      value: summary.total_reduction_fmt || '00:00', color: '#2e7d32', icon: <TrendingUp /> },
                  { label: 'Net Deduction',      value: summary.total_deduction_fmt || '00:00', color: '#e65100', icon: <TrendingDown /> },
                  { label: 'Total Fines',        value: summary.total_fines > 0 ? `₹${parseFloat(summary.total_fines).toLocaleString()}` : '₹0', color: '#b71c1c', icon: <Warning /> },
                ].map((item, i, arr) => (
                  <Box key={item.label}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, py: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ p: 0.8, borderRadius: 1.5, background: item.color + '15' }}>
                          {React.cloneElement(item.icon, { sx: { color: item.color, fontSize: 18 } })}
                        </Box>
                        <Typography fontSize={13} fontWeight={600} color="#444">{item.label}</Typography>
                      </Box>
                      <Typography fontSize={15} fontWeight={800} color={item.color}>{item.value || '00:00'}</Typography>
                    </Box>
                    {i < arr.length - 1 && <Divider />}
                  </Box>
                ))}
              </CardContent>
            </Card>
          )}
        </>
      )}
    </Box>
  );
}
